import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';

export enum PublishedAttributeOwnerType {
  CLIENT = 'client',
  SYSTEM = 'system',
}

export type PublishedAttributeFormatted = {
  attributeName: string;
  attributeToken: string;
  outputCategory?: string;
  dataType: 'string' | 'integer' | 'decimal' | 'boolean' | 'timestamp' | 'date';
  defaultValue: string;
  defaultValueNote: string;
  description?: string;
  valueExpiresInDays?: number | null;
  containsPii?: boolean;
  jqFilter?: string;
  archived: boolean;
  activeWorkflows?: {
    name: string;
    active_public_version: number;
    application_token: string;
  }[];
  hasExpiration: boolean;
  hasJqFilter: boolean;
  ownerType: PublishedAttributeOwnerType;
  objectType: string;
  validValueMin?: string | null;
  validValueMax?: string | null;
  validValues?: string | null;
  agentCanCreateValue?: boolean;
  groupingToken?: string;
  defineValueRanges?: boolean;
  outputValueRange?: OutputValueRange[];
  outputValueRangeOptions?: ValueRangeConfigOptions;
};

export type PublishedAttributeUnformatted = {
  published_attribute_name: string;
  published_attribute_token?: string;
  data_type: string;
  default_value: string | number | boolean;
  contains_pii: boolean;
  object_type: string;
  service_name: string | null;
  value_expires_in?: number;
  jq_filter?: string;
  archived: boolean;
  description?: string;
  default_value_note?: string;
  active_workflows?: {
    name: string;
    active_public_version: number;
    application_token: string;
  }[];
  owner_type: PublishedAttributeOwnerType;
  valid_value_min?: string | number | null;
  valid_value_max?: string | number | null;
  valid_values?: string[];
  agent_can_create_value?: boolean;
  grouping_token?: string;
  _embedded: {
    grouping: {
      grouping_token: string;
    } | null;
  };
};

export type PublishedAttributeGroupingFormatted = {
  groupingToken: string;
  groupingName: string;
  archived: boolean;
  createdAt: number;
  groupingOrder: number | null;
  publishedAttributes?: {
    token: string;
    name: string;
  }[];
  groupingAttributeTokens?: string[];
};

export type PublishedAttributeGroupingUnformatted = {
  grouping_token: string;
  name: string;
  archived: boolean;
  created_at: number;
  order: number | null;
  _embedded: {
    published_attributes: {
      published_attribute_token: string;
      name: string;
    }[];
  };
  published_attribute_tokens?: string[];
};

export type TableProps = {
  attributes: {
    data: PublishedAttributeFormatted[];
    isLoading: boolean;
    error: boolean;
  };
  attributeGroupings: {
    data: PublishedAttributeGroupingFormatted[];
    isLoading: boolean;
    error: boolean;
  };
};

export const PUBLISHED_ATTRIBUTE_DATA_TYPES = [
  'string',
  'integer',
  'decimal',
  'boolean',
  'timestamp',
  'date',
] as const;

export type PublishedAttributeDataType = typeof PUBLISHED_ATTRIBUTE_DATA_TYPES[number];
